import Keycloak from "keycloak-js"
import { userStore } from "../store/userStore"
import { organizationStore } from "../store/organizationStore"
import {
  setLocalStoragewithoutStringify,
  clearLocalStorage,
  STORAGE_KEYS,
} from "../Helper/localStorageHandler"

const authEnabled = process.env.REACT_APP_AUTH_ENABLED === "true"
const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL
const keycloakRealm = process.env.REACT_APP_KEYCLOAK_REALM
const keycloakClientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID

let kc = null
let keycloakUserId = null
let userEmail = null
let isInitializing = false

const handleLogout = () => {
  const logoutRedirect =
    process.env.REACT_APP_Redirect_URL || window.location.origin
  clearLocalStorage() // Clear all session data
  userStore.getState().clearUserData() // Clear in-memory user store
  organizationStore.getState().clearOrgs() // Clear organization store
  if (kc && kc.didInitialize) {
    kc.logout({ redirectUri: logoutRedirect })
  } else {
    window.location.href = logoutRedirect
  }
}

if (authEnabled) {
  const initOptions = {
    url: keycloakUrl,
    realm: keycloakRealm,
    clientId: keycloakClientId,
  }
  kc = new Keycloak(initOptions)

  // The backend force-ends a user's session when their role changes, instead of
  // silently rotating claims. When a refresh subsequently fails (or Keycloak
  // reports the session as logged out), don't fail silently - send the user
  // back to login so they pick up their new permissions.
  kc.onAuthRefreshError = () => {
    console.error(
      "Keycloak token refresh failed - session may have been revoked, redirecting to login",
    )
    handleLogout()
  }
  kc.onAuthLogout = () => {
    console.error("Keycloak session ended, redirecting to login")
    handleLogout()
  }
} else {
  console.log("Authentication disabled for testing")
}

const getRegistrationSuccessRedirectUri = () => {
  const baseRedirect =
    process.env.REACT_APP_Redirect_URL || window.location.origin
  const redirectUrl = new URL(baseRedirect, window.location.origin)
  redirectUrl.searchParams.set("registration", "success")
  return redirectUrl.toString()
}

const clearRegistrationSuccessQueryParam = () => {
  const currentUrl = new URL(window.location.href)
  if (currentUrl.searchParams.get("registration") !== "success") return

  currentUrl.searchParams.delete("registration")
  const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
  window.history.replaceState({}, document.title, nextUrl)
}

/**
 * Initialize Keycloak and handle authentication setup.
 * Same pattern as smart_home_admin_app/src/keycloakConfig.js
 */
const initializeKeycloak = (onInitialized) => {
  return new Promise((resolve, reject) => {
    if (!authEnabled || !kc) {
      onInitialized()
      return resolve(true)
    }

    // Support React 18 Strict Mode double-invocation
    if (kc.didInitialize) {
      onInitialized()
      return resolve(kc.authenticated)
    }

    if (isInitializing) {
      // Just waiting for the original initialization to finish
      return
    }
    isInitializing = true

    kc.init({
      onLoad: "login-required",
      checkLoginIframe: false,
      pkceMethod: "S256",
    })
      .then((authenticated) => {
        if (authenticated) {
          // Check if the user was just created (registration flow)
          const tokenParsed = kc.tokenParsed
          const now = Date.now()
          const createdAt = tokenParsed?.created_at

          // If 'created_at' exists and is within the last 15 seconds, they just registered.
          // We log them out immediately to force them to the login screen.
          if (createdAt && now - createdAt < 15000) {
            const logoutRedirect = getRegistrationSuccessRedirectUri()
            kc.logout({ redirectUri: logoutRedirect })
            onInitialized()
            return resolve(false)
          }

          clearRegistrationSuccessQueryParam()
          setLocalStoragewithoutStringify(STORAGE_KEYS.ACCESS_TOKEN, kc.token)
          keycloakUserId = tokenParsed?.sub || null
          userEmail = tokenParsed?.email || null

          userStore.getState().setUserData({
            keycloakUserId,
            userId: keycloakUserId,
            userEmail,
            tenantId: tokenParsed?.tenantId || null,
            name:
              tokenParsed?.name ||
              `${tokenParsed?.given_name || ""} ${tokenParsed?.family_name || ""}` ||
              null,
          })

          kc.onTokenExpired = () => {
            kc.updateToken(30)
              .then((refreshed) => {
                if (refreshed) {
                  setLocalStoragewithoutStringify(
                    STORAGE_KEYS.ACCESS_TOKEN,
                    kc.token,
                  )
                  // Re-sync permissions on every normal refresh in case the
                  // user's role/permissions changed server-side.
                  userStore.getState().fetchUserPermissions(true)
                }
              })
              .catch(() => {
                // kc.onAuthRefreshError handles redirecting to login
                console.error("Failed to refresh token")
              })
          }
        }
        onInitialized()
        resolve(authenticated)
      })
      .catch((error) => {
        console.error("Keycloak initialization failed:", error)
        onInitialized()
        reject(error)
      })
  })
}

export {
  kc,
  authEnabled,
  keycloakUserId,
  userEmail,
  initializeKeycloak,
  handleLogout,
}
